import React, { useState } from 'react';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  tableWrapper: {
    maxHeight: '500px',
    overflowY: 'auto',
    overflowX: 'auto',
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: '2px',
  },
  headerCell: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: '#fff',
    padding: '0 4px',
    height: '60px',
    verticalAlign: 'bottom',
    textAlign: 'center',
  },
  headerLabel: {
    display: 'inline-block',
    transform: 'rotate(-45deg)',
    transformOrigin: 'bottom left',
    whiteSpace: 'nowrap',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  cornerCell: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 3,
    background: '#fff',
    minWidth: '56px',
  },
  rowHeader: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: '#fff',
    padding: '0 10px 0 0',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  cell: {
    width: '60px',
    minWidth: '60px',
    height: '35px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '4px',
    cursor: 'default',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    border: '2px solid transparent',
    padding: 0,
  },
  summaryRowHeader: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: '#f3f4f6',
    padding: '0 10px 0 0',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: 700,
    color: '#1f2937',
    whiteSpace: 'nowrap',
  },
  summaryCell: {
    width: '60px',
    minWidth: '60px',
    height: '35px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 700,
    borderRadius: '4px',
    cursor: 'default',
    background: '#f3f4f6',
    padding: 0,
    border: '2px solid transparent',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
};

// Color gradient stops: cv -> color
const COLOR_STOPS = [
  { cv: 0, color: [255, 255, 255] },    // #ffffff
  { cv: 2, color: [254, 243, 199] },    // #fef3c7
  { cv: 5, color: [254, 215, 170] },    // #fed7aa
  { cv: 10, color: [252, 165, 165] },   // #fca5a5
  { cv: 15, color: [220, 38, 38] },     // #dc2626
];

function interpolateColor(cv) {
  if (cv <= 0) return 'rgb(255, 255, 255)';

  for (let i = 1; i < COLOR_STOPS.length; i++) {
    if (cv <= COLOR_STOPS[i].cv) {
      const prev = COLOR_STOPS[i - 1];
      const curr = COLOR_STOPS[i];
      const t = (cv - prev.cv) / (curr.cv - prev.cv);
      const r = Math.round(prev.color[0] + t * (curr.color[0] - prev.color[0]));
      const g = Math.round(prev.color[1] + t * (curr.color[1] - prev.color[1]));
      const b = Math.round(prev.color[2] + t * (curr.color[2] - prev.color[2]));
      return `rgb(${r}, ${g}, ${b})`;
    }
  }

  // cv > 15: clamp to the reddest stop
  return 'rgb(220, 38, 38)';
}

function getTextColor(cv) {
  return cv > 10 ? '#ffffff' : '#1f2937';
}

function computeOverallCV(matrix, states, fieldKey) {
  // Aggregate across all states for a given field
  let sumValues = 0;
  let sumSqValues = 0;
  let totalCount = 0;

  for (const state of states) {
    const cell = matrix[state]?.[fieldKey];
    if (!cell || cell.cv == null || cell.count === 0) continue;
    // Use mean and count to reconstruct sum and sumSq
    // mean = sum / count, stdDev = sqrt(sumSq/count - mean^2)
    // sum = mean * count
    // sumSq = (stdDev^2 + mean^2) * count
    const { mean, stdDev, count } = cell;
    sumValues += mean * count;
    sumSqValues += (stdDev * stdDev + mean * mean) * count;
    totalCount += count;
  }

  if (totalCount === 0) return null;
  const overallMean = sumValues / totalCount;
  if (overallMean === 0) return { cv: 0, mean: 0, stdDev: 0, count: totalCount };
  const variance = sumSqValues / totalCount - overallMean * overallMean;
  const overallStdDev = Math.sqrt(Math.max(0, variance));
  const cv = (overallStdDev / overallMean) * 100;
  return { cv, mean: overallMean, stdDev: overallStdDev, count: totalCount };
}

function computeStateAvgCV(matrix, state, fields) {
  let sum = 0;
  let count = 0;
  for (const f of fields) {
    const cell = matrix[state]?.[f.key];
    if (cell && cell.cv != null) {
      sum += cell.cv;
      count++;
    }
  }
  if (count === 0) return null;
  return sum / count;
}

function computeOverallAllFields(matrix, states, fields) {
  // Average of all overall CVs across fields
  let sum = 0;
  let count = 0;
  for (const f of fields) {
    const overall = computeOverallCV(matrix, states, f.key);
    if (overall && overall.cv != null) {
      sum += overall.cv;
      count++;
    }
  }
  if (count === 0) return null;
  return sum / count;
}

export default function VolatilityMatrix({ data }) {
  const [tooltip, setTooltip] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Volatility Matrix</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No volatility data available
        </div>
      </div>
    );
  }

  const { states, fields, matrix, maxCV } = data;

  const handleMouseEnter = (e, state, field, cellData, isSummary) => {
    setHoveredCell(`${state}-${field}`);
    setTooltip({
      x: e.clientX + 12,
      y: e.clientY - 10,
      state,
      field,
      cellData,
      isSummary,
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip) {
      setTooltip((prev) => prev ? { ...prev, x: e.clientX + 12, y: e.clientY - 10 } : null);
    }
  };

  const handleMouseLeave = () => {
    setHoveredCell(null);
    setTooltip(null);
  };

  const getCellStyle = (cv, cellKey, isSummaryCol) => {
    const isHovered = hoveredCell === cellKey;
    const base = isSummaryCol ? { ...styles.summaryCell } : { ...styles.cell };

    if (cv == null) {
      return {
        ...base,
        background: '#f9fafb',
        color: '#9ca3af',
        ...(isHovered ? { border: '2px solid #2563eb', transform: 'scale(1.05)' } : {}),
      };
    }

    return {
      ...base,
      background: isSummaryCol ? '#f3f4f6' : interpolateColor(cv),
      color: isSummaryCol ? '#1f2937' : getTextColor(cv),
      ...(isHovered ? { border: '2px solid #2563eb', transform: 'scale(1.05)' } : {}),
    };
  };

  // Precompute overall row data
  const overallByField = {};
  for (const f of fields) {
    overallByField[f.key] = computeOverallCV(matrix, states, f.key);
  }

  const overallAllFields = computeOverallAllFields(matrix, states, fields);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Volatility Matrix</div>
      <div style={styles.subtitle}>
        Coefficient of variation (%) by state and price field — darker = more volatile
      </div>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {/* Corner cell */}
              <th style={styles.cornerCell} />
              {/* Field column headers */}
              {fields.map((f) => (
                <th key={f.key} style={styles.headerCell}>
                  <div style={styles.headerLabel}>{f.label}</div>
                </th>
              ))}
              {/* All Fields summary column header */}
              <th style={{ ...styles.headerCell, background: '#f3f4f6' }}>
                <div style={{ ...styles.headerLabel, color: '#1f2937' }}>All Fields</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* State rows */}
            {states.map((state) => {
              const avgCV = computeStateAvgCV(matrix, state, fields);
              return (
                <tr key={state}>
                  <td style={styles.rowHeader}>{state}</td>
                  {fields.map((f) => {
                    const cell = matrix[state]?.[f.key];
                    const hasData = cell && cell.cv != null;
                    const cellKey = `${state}-${f.key}`;
                    return (
                      <td
                        key={f.key}
                        style={getCellStyle(hasData ? cell.cv : null, cellKey, false)}
                        onMouseEnter={(e) =>
                          handleMouseEnter(e, state, f.label, hasData ? cell : null, false)
                        }
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                      >
                        {hasData ? `${cell.cv.toFixed(1)}%` : '\u2014'}
                      </td>
                    );
                  })}
                  {/* All Fields summary column */}
                  <td
                    style={getCellStyle(avgCV, `${state}-allfields`, true)}
                    onMouseEnter={(e) =>
                      handleMouseEnter(
                        e,
                        state,
                        'All Fields',
                        avgCV != null ? { cv: avgCV } : null,
                        true
                      )
                    }
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    {avgCV != null ? `${avgCV.toFixed(1)}%` : '\u2014'}
                  </td>
                </tr>
              );
            })}

            {/* Overall summary row */}
            <tr>
              <td style={styles.summaryRowHeader}>Overall</td>
              {fields.map((f) => {
                const overall = overallByField[f.key];
                const hasData = overall && overall.cv != null;
                const cellKey = `overall-${f.key}`;
                return (
                  <td
                    key={f.key}
                    style={getCellStyle(hasData ? overall.cv : null, cellKey, true)}
                    onMouseEnter={(e) =>
                      handleMouseEnter(e, 'Overall', f.label, hasData ? overall : null, true)
                    }
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    {hasData ? `${overall.cv.toFixed(1)}%` : '\u2014'}
                  </td>
                );
              })}
              {/* Overall + All Fields corner */}
              <td
                style={getCellStyle(overallAllFields, 'overall-allfields', true)}
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    'Overall',
                    'All Fields',
                    overallAllFields != null ? { cv: overallAllFields } : null,
                    true
                  )
                }
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                {overallAllFields != null ? `${overallAllFields.toFixed(1)}%` : '\u2014'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fff', border: '1px solid #e5e7eb' }} />
          0%
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fef3c7' }} />
          2%
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fed7aa' }} />
          5%
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fca5a5' }} />
          10%
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#dc2626' }} />
          15%+
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state} — {tooltip.field}
          </div>
          {tooltip.cellData ? (
            <>
              <div style={{ color: '#374151', marginBottom: '2px' }}>
                CV: <strong>{tooltip.cellData.cv.toFixed(2)}%</strong>
              </div>
              {tooltip.cellData.mean != null && (
                <div style={{ color: '#6b7280', marginBottom: '2px' }}>
                  Mean: ${tooltip.cellData.mean.toFixed(2)}
                </div>
              )}
              {tooltip.cellData.stdDev != null && (
                <div style={{ color: '#6b7280', marginBottom: '2px' }}>
                  Std Dev: ${tooltip.cellData.stdDev.toFixed(2)}
                </div>
              )}
              {tooltip.cellData.count != null && (
                <div style={{ color: '#6b7280' }}>
                  Samples: {tooltip.cellData.count}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>No data available</div>
          )}
        </div>
      )}
    </div>
  );
}
